"use client";

import { LayoutServiceData } from "@sitecore-jss/sitecore-jss-nextjs";
import { useLDClient } from "launchdarkly-react-client-sdk";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { extractUTMParameters } from "../../helpers/extractUTMParameters";
import { isBrowser } from "../../helpers/isBrowser";
import { Property } from "../../types/property-types";
import { PageViewEventData } from "./AnalyticData";
import { event as personalizeEvent } from "@sitecore-cloudsdk/events/browser";
import useCloudSDK from "@greystar/ui/src/hooks/useCloudSDK";

declare global {
  interface Window {
    dataLayer?: Object[];
  }
}

type TrackExperimentProps = {
  experimentId: string;
  variantName: string;
  eventData?: Record<string, unknown>;
};

export function pushDataLayer(analyticsEvent: any) {
  if (!isBrowser) return false;

  const newEvent =
    typeof analyticsEvent === "string"
      ? JSON.parse(analyticsEvent)
      : analyticsEvent;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(newEvent);
  return null;
}

export type TrackEventFunction = (
  event: string,
  eventData: Record<string, any>,
) => Promise<void>;

export const AnalyticsContext = createContext<
  | {
      trackEvent: TrackEventFunction;
      trackExperimentEvent: (props: TrackExperimentProps) => Promise<void>;
    }
  | undefined
>(undefined);

type AnalyticsProviderProps = {
  children: React.ReactNode;
  sitecoreData: LayoutServiceData["sitecore"];
};

export const AnalyticsProvider = ({
  children,
  sitecoreData,
}: AnalyticsProviderProps) => {
  const ldClient = useLDClient();
  const cloudSDK = useCloudSDK();
  const [isPageViewTriggerd, setIsPageViewTriggerd] = useState(false);

  const getUserAgent = () => {
    return {
      sdk_name: "react-client-sdk",
      user_agent: isBrowser ? navigator.userAgent : "",
      sdk_version: "3.6.1",
      event_source: "browser-sdk",
      referrer: isBrowser ? document.referrer : "",
      "application name": null,
      "application version": null,
      "application identifier": null,
      "application version name": null,
    };
  };

  const value = useMemo(() => {
    const trackEvent: TrackEventFunction = async (eventName, eventData) => {
      pushDataLayer({ event: eventName, ...eventData });

      if (cloudSDK.isCloudSDKLoaded) {
        const personalizeEventData = {
          type: eventName,
          extensionData: {
            ...eventData,
          },
        };

        personalizeEvent(personalizeEventData);
      }

      if (ldClient) {
        const utms = extractUTMParameters();
        const userAgent = getUserAgent();
        const ldMetadata = {
          ...eventData,
          ...userAgent,
          ...utms,
        };

        ldClient.track(eventName, ldMetadata);

        try {
          await ldClient.flush();
        } catch (error) {
          console.error("LaunchDarkly flush error:", error);
        }
      }
    };

    const trackExperimentEvent = async ({
      experimentId,
      variantName,
      eventData = {},
    }: TrackExperimentProps) =>
      trackEvent("experiment_displayed", {
        experimentId,
        variantName,
        ...eventData,
      });

    return { trackEvent, trackExperimentEvent };
  }, [ldClient, cloudSDK.isCloudSDKLoaded]);

  useEffect(() => {
    if (!isBrowser || !ldClient) return;

    const property = sitecoreData?.context?.property as Property;

    const fullAddress = property?.location;
    const formattedAddress = fullAddress
      ? `${fullAddress.address}, ${fullAddress.city}, ${fullAddress.state}, ${fullAddress.postalCode}`
      : "";
    const pageType = `${sitecoreData.route?.templateName ?? "unknown"} | ${sitecoreData.context.site?.name ?? "unknown"}`;
    const ldUser = ldClient.getContext();
    const userAgent = getUserAgent();
    const utms = extractUTMParameters();

    const pageViewObj = {
      ...PageViewEventData,
      domain: window.location.hostname,
      page_location: window.location.href,
      page_title: document.title,
      page_type: pageType,
      country: fullAddress?.countryCode ?? "",
      state: fullAddress?.state ?? "",
      property_id: property?.id ?? "",
      property_name: property?.name ?? "",
      property_location: formattedAddress ?? "",
      ld_user_id:
        ldUser && typeof ldUser === "object" ? (ldUser?.key ?? "") : "",
      website_version: "redesign",
      ...userAgent,
      ...utms,
    };

    if (!isPageViewTriggerd) {
      pushDataLayer(pageViewObj);
      ldClient.track(pageViewObj.event, pageViewObj);

      setIsPageViewTriggerd(true);
    }
  }, [ldClient, isPageViewTriggerd, sitecoreData]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};
